import React, { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router';
import { Trans } from 'react-i18next';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { ContentParagraph, ContentParagraphModifier } from '../ContentParagraph';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import Hyperlink from '../../../components/atoms/Hyperlink/Hyperlink';
import { Strong } from '../../../components/atoms/Strong/Strong';
import { PageLockForceReleaseForm } from './PageLockForceReleaseForm';

export function PageLockedModal() {
  const { t, i18n } = useSonTranslation('PageLockedModal');
  const navigate = useNavigate();
  const location = useLocation();
  const { pageInfo } = useAppContext();
  const preventNavigation = useRef<boolean>(false);

  const noneEditModeUrlOfPage = useMemo(() => {
    // or part added to keep navigation working also for start page which returned empty string before [SIBZIEL-11869]
    return location.pathname.replace(/\/bearbeitungsmodus$/g, '') || '/';
  }, [location.pathname]);

  const closeModal = useCallback(() => {
    if (!preventNavigation.current) {
      navigate(noneEditModeUrlOfPage);
    }
  }, [navigate, noneEditModeUrlOfPage, preventNavigation]);

  return (
    <ModalDialog headline={t('pageLockedHeadline')} closeModal={closeModal} initialFocusIndex={3}>
      <div className="pagemanagement__page-locked-container">
        {pageInfo?.pageLockedBy && (
          <ContentParagraph modifier={ContentParagraphModifier.MARGIN_BOTTOM}>
            <Trans
              i18nKey="phrases:pageLocked"
              i18n={i18n}
              values={{ name: pageInfo?.pageLockedBy?.name }}
              components={[
                // eslint-disable-next-line react/jsx-key
                <Strong>
                  <Hyperlink
                    className="global--anchor"
                    routing
                    to={pageInfo?.pageLockedBy?.url}
                    onClick={() => {
                      preventNavigation.current = true;
                    }}
                  >
                    {/* This is a placeholder for the text provided by translation */}
                  </Hyperlink>
                </Strong>,
              ]}
            />
          </ContentParagraph>
        )}

        {!pageInfo?.forcePageLockReleaseUrl
          ? (
              <ContentParagraph modifier={ContentParagraphModifier.MARGIN_BOTTOM}>
                {t('pageLockReleaseHint')}
              </ContentParagraph>
            )
          : null}
        <div className={`page-locked-modal${pageInfo?.forcePageLockReleaseUrl ? '' : ' page-locked-modal--btn-right'}`}>
          <span className="page-locked-modal__close">
            <Btn href={noneEditModeUrlOfPage} modifier={[BtnModifier.SINGLE_ACTION, BtnModifier.TRANSPARENT]}>
              {t('close')}
            </Btn>
          </span>
          {pageInfo?.forcePageLockReleaseUrl
            ? (
                <PageLockForceReleaseForm forcePageLockReleaseUrl={pageInfo.forcePageLockReleaseUrl} />
              )
            : null}
        </div>
      </div>
    </ModalDialog>
  );
}
